import { EnumButtonName } from '@calm/evexia-analytics';
import { DS_SHAPE_BORDER_RADIUS } from '@calm/evexia-design-tokens';
import {
    Box,
    ButtonBase,
    ButtonBaseProps,
    Grid,
    Paper,
    SxProps,
    Theme,
    TypographyProps,
} from '@rhg/libs/material';
import { sxAppend, ValueByBreakpoint } from '@rhg/libs/material/sx';
import { StaticImageData } from '@rhg/libs/next/image';
import { ReactNode, useMemo, useState } from 'react';
import CardContent from './card-content';
import CardImage, { CardImageProps } from './card-image';
import { CardVariant } from './types';

const cardBannerImageMaxHeight = 700;

const stylePaper = { overflow: 'hidden', height: '100%' };
const styleButtonAndCardContainer = { position: 'relative' };
const styleOverlaidButton = {
    zIndex: 2,
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
};
const styleHorizontalContentWrapper = { flexGrow: 1 };

export interface CardProps {
    /** Card title, most prominent text */
    title: string;
    /** The maximum number of lines that the title is going to span */
    titleMaxLines?: number;
    /** Component containing card title, used to pass in headings if needed */
    titleComponent?: React.ElementType;
    /** The spacing units separating the title container and the category container */
    titleTopSpacing?: number;
    /** The spacing units separating the title and the subtitle */
    titleBottomSpacing?: number;
    /** Card subtitle appearing under the title and progress */
    subtitle?: string;
    /** The subtitle's text variant */
    subtitleVariant?: TypographyProps['variant'];
    /** The maximum number of lines that the subtitle is going to span */
    subtitleMaxLines?: number;
    /** icon appearing next to the card title */
    titleIcon?: ReactNode;
    /** Appears above category info on large cards, otherwise is used in imageIconTop */
    contentTag?: string;
    /** Appears besides content and allows you to do an action */
    contentAction?: ReactNode;
    /** string describing the category of this card */
    categoryTitle?: string;
    /** The color of the category title. Should be a MUI system color. */
    categoryTitleColor?: string;
    /** icon which appears next to the category */
    categoryIcon?: ReactNode;
    /** describes the category of this card appears next to the category */
    categoryDescription?: string;
    /** decimal value describing the progress and shown as a filled progress bar (between 0-1) */
    progress?: number;
    /** text describing the progress and shown as a label of the progress bar */
    progressText?: string;
    /** The alt text to be used on the linear progress bar, in the case that it should display a different value than the progressText */
    progressTextAlt?: string;
    /** appears in the content under the title and subtitle */
    buttonGroup?: ReactNode;
    /** Colors the Paper component that this card is on */
    backgroundColor?: string;
    /** icon which overlays the image on focus */
    imageFocusIcon?: ReactNode;
    /** image source should be 3:2 w:h dimensions */
    imageSrc: string | StaticImageData;
    /** image alt description */
    imageAlt: string;
    /** icon and/or text overlaying the top left portion of the image */
    imageIconTop?: ReactNode;
    /** icon and/or text overlaying the bottom left portion of the image */
    imageIconBottom?: ReactNode;
    /** Forced width of image */
    imageForcedSize?: number | string;
    imageWidth?: Partial<ValueByBreakpoint<number | string>>;
    imageShowIconsOnFocused?: boolean;
    /** Determines if the images will have curvy corners on the right side */
    imageSideRadiusEnabled?: boolean;
    isEntireCardClickable?: boolean;
    entireCardButtonProps?: Partial<
        ButtonBaseProps & { component?: React.ElementType; href?: string } & {
            'data-cy'?: string;
        }
    >;
    CardImageProps?: Partial<CardImageProps>;
    variant: CardVariant;
    isNotClickable?: boolean;
    onClick?: () => void;
    /** Card label if it is a button */
    label?: string;
    /** Tracking for clicking on the image if it is clickable */
    trackButton?: EnumButtonName;
    sx?: SxProps<Theme>;
    /** Styles applied to the card content */
    contentSx?: SxProps<Theme>;
    /** The spacing units applied to to top of the button in case of a banner card */
    buttonTopSpacing?: number;
    /** An additional action, apart from the onClick action. Should be a button element. */
    additionalAction?: ReactNode;
    /** Aria overrides, used for testing */
    role?: string;
    'aria-label'?: string;
    /** Test ID used for synthetic tests */
    'data-syntheticid'?: string;
}

type CardContainerProps = Pick<
    CardProps,
    | 'role'
    | 'trackButton'
    | 'role'
    | 'label'
    | 'isNotClickable'
    | 'entireCardButtonProps'
    | 'isEntireCardClickable'
    | 'backgroundColor'
    | 'sx'
>;

interface ClickContainerProps extends CardContainerProps {
    ariaLabel?: string;
    clickHandlers?: {
        onMouseEnter: () => void;
        onMouseLeave: () => void;
        onFocus: () => void;
        onBlur: () => void;
        onClick: (() => void) | undefined;
    };
    children: ReactNode;
}

export const CardContainer = ({
    ariaLabel,
    role,
    trackButton,
    label,
    entireCardButtonProps,
    backgroundColor,
    isEntireCardClickable,
    isNotClickable,
    clickHandlers,
    sx,
    children,
    ...props
}: ClickContainerProps) => {
    const paperClickHandlers = useMemo(() => {
        if (isNotClickable) {
            return {};
        }
        if (isEntireCardClickable) {
            return clickHandlers;
        }
        return {};
    }, [clickHandlers, isEntireCardClickable, isNotClickable]);

    return (
        <>
            {isEntireCardClickable ? (
                <Box
                    sx={sxAppend(styleButtonAndCardContainer, sx)}
                    aria-label={ariaLabel}
                    role={role}
                    {...props}
                >
                    <ButtonBase
                        trackButton={trackButton ?? 'NotMapped'}
                        tabIndex={0}
                        aria-label={label}
                        sx={styleOverlaidButton}
                        {...paperClickHandlers}
                        {...entireCardButtonProps}
                    />
                    <Paper
                        sx={sxAppend(
                            stylePaper,
                            !!backgroundColor && { backgroundColor }
                        )}
                    >
                        {children}
                    </Paper>
                </Box>
            ) : (
                <Paper
                    sx={sxAppend(
                        stylePaper,
                        !!backgroundColor && { backgroundColor },
                        sx
                    )}
                    aria-label={ariaLabel}
                    role={role}
                    {...props}
                >
                    {children}
                </Paper>
            )}
        </>
    );
};

export default function Card({
    title,
    titleMaxLines,
    titleComponent,
    titleTopSpacing,
    titleBottomSpacing,
    subtitle,
    subtitleVariant,
    subtitleMaxLines,
    titleIcon,
    contentTag,
    contentAction,
    categoryTitle,
    categoryTitleColor,
    categoryIcon,
    categoryDescription,
    progress,
    progressText,
    progressTextAlt,
    buttonGroup,
    backgroundColor,
    imageFocusIcon,
    imageSrc,
    imageAlt,
    imageIconTop,
    imageIconBottom,
    imageForcedSize,
    imageWidth,
    imageShowIconsOnFocused,
    imageSideRadiusEnabled,
    isEntireCardClickable,
    entireCardButtonProps,
    variant,
    onClick,
    label,
    trackButton,
    sx,
    CardImageProps,
    contentSx,
    buttonTopSpacing,
    additionalAction,
    isNotClickable,
    role,
    'aria-label': ariaLabel,
    'data-syntheticid': dataSyntheticId,
}: CardProps) {
    const [hasFocus, setHasFocus] = useState(false);

    const clickHandlers = useMemo(
        () => ({
            onMouseEnter: () => setHasFocus(true),
            onMouseLeave: () => setHasFocus(false),
            onFocus: () => setHasFocus(true),
            onBlur: () => setHasFocus(false),
            onClick: onClick,
        }),
        [onClick]
    );

    const imageClickHandlers = useMemo(() => {
        if (isNotClickable) {
            return {};
        }

        if (isEntireCardClickable) {
            return {};
        }
        return clickHandlers;
    }, [clickHandlers, isEntireCardClickable, isNotClickable]);

    return (
        <CardContainer
            clickHandlers={clickHandlers}
            role={role}
            label={label}
            ariaLabel={ariaLabel}
            isNotClickable={isNotClickable}
            entireCardButtonProps={entireCardButtonProps}
            isEntireCardClickable={isEntireCardClickable}
            sx={sx}
            backgroundColor={backgroundColor}
            trackButton={trackButton}
            data-syntheticid={dataSyntheticId}
        >
            <Grid
                container
                alignItems="center"
                wrap={variant === 'horizontal' ? 'nowrap' : undefined}
            >
                <Grid
                    item
                    xs={variant === 'horizontal' ? undefined : 12}
                    sm={variant === 'horizontal' ? undefined : 12}
                    md={variant === 'horizontal' ? undefined : 12}
                    lg={
                        variant === 'banner' || variant === 'bannerSquare'
                            ? 6
                            : variant === 'horizontal'
                            ? undefined
                            : 12
                    }
                >
                    <CardImage
                        src={imageSrc}
                        alt={imageAlt}
                        sx={
                            imageForcedSize
                                ? { width: imageForcedSize }
                                : undefined
                        }
                        iconTop={imageIconTop}
                        iconBottom={imageIconBottom}
                        iconFocus={imageFocusIcon}
                        width={
                            typeof imageForcedSize === 'number'
                                ? imageForcedSize
                                : imageWidth
                        }
                        maxHeight={
                            variant === 'banner' || variant === 'bannerSquare'
                                ? cardBannerImageMaxHeight
                                : undefined
                        }
                        isFocused={hasFocus}
                        bezel={
                            variant === 'horizontal'
                                ? DS_SHAPE_BORDER_RADIUS.CARD
                                : 0
                        }
                        imageSideRadiusEnabled={imageSideRadiusEnabled}
                        showIconsOnFocused={imageShowIconsOnFocused}
                        ratio={
                            [
                                'carouselSquare',
                                'bannerSquare',
                                'horizontal',
                            ].includes(variant)
                                ? '1:1'
                                : '3:2'
                        }
                        {...imageClickHandlers}
                        aria-label={onClick && label ? label : undefined}
                        trackButton={trackButton}
                        {...CardImageProps}
                    />
                </Grid>
                <Grid
                    item
                    xs={variant === 'horizontal' ? undefined : 12}
                    sm={variant === 'horizontal' ? undefined : 12}
                    md={variant === 'horizontal' ? undefined : 12}
                    lg={
                        variant === 'banner' || variant === 'bannerSquare'
                            ? 6
                            : variant === 'horizontal'
                            ? undefined
                            : 12
                    }
                    sx={
                        variant === 'horizontal'
                            ? styleHorizontalContentWrapper
                            : undefined
                    }
                >
                    <CardContent
                        title={title}
                        titleComponent={titleComponent}
                        titleMaxLines={titleMaxLines}
                        titleTopSpacing={titleTopSpacing}
                        titleBottomSpacing={titleBottomSpacing}
                        subtitle={subtitle}
                        subtitleVariant={subtitleVariant}
                        subtitleMaxLines={subtitleMaxLines}
                        icon={titleIcon}
                        tag={contentTag}
                        action={contentAction}
                        categoryTitle={categoryTitle}
                        categoryTitleColor={categoryTitleColor}
                        categoryDescription={categoryDescription}
                        categoryIcon={categoryIcon}
                        progress={progress}
                        progressText={progressText}
                        progressTextAlt={progressTextAlt}
                        buttonGroup={buttonGroup}
                        variant={variant}
                        sx={contentSx}
                        buttonTopSpacing={
                            variant === 'banner' || variant === 'bannerSquare'
                                ? buttonTopSpacing
                                : undefined
                        }
                        additionalAction={additionalAction}
                    />
                </Grid>
            </Grid>
        </CardContainer>
    );
}
